import React, { useState, lazy, Suspense } from 'react';
import {
  Upload, Trash2, Music, FileText, Download, Edit2, File, Bell,
  Lightbulb, GripVertical, ChevronDown, ChevronRight, MessageCircle,
  Paperclip
} from 'lucide-react';
import VersionPlayer from './VersionPlayer';
import SongLyricsPanel from './SongLyricsPanel';
import SongFilesPanel from './SongFilesPanel';
import DragDropZone from '../DragDropZone';

// Lazy-load the preview modal — pulls in pdf.js/mammoth/markdown only when needed
const DocumentPreview = lazy(() => import('../Documents/DocumentPreview'));

export default function SongCard({
  song,
  index,
  isExpanded,
  onToggleExpand,
  isMobile,
  isPremium,
  user,
  // From useSongs hook
  ideas,
  uploadProgress,
  addingFromIdea,
  commenterName,
  canAddVersion,
  getVersionCount,
  getVersionsArray,
  getUnreadCommentCount,
  // Actions
  onUpdateSongName,
  onDeleteSong,
  onAudioUpload,
  onLyricsUpload,
  onDeleteLyrics,
  onUpdateLyricsText,
  onUploadSongFile,
  onDeleteSongFile,
  onDeleteVersion,
  onUpdateNotes,
  onAddComment,
  onDeleteComment,
  onAddIdeaToSong,
  // Drag handlers
  onMouseDown,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  // Drag state
  draggedIndex,
  insertIndex,
  // Constants
  FREE_VERSION_LIMIT,
  loading
}) {
  const [editingSong, setEditingSong] = useState(false);
  const [editName, setEditName] = useState(song.name);
  const [editTempo, setEditTempo] = useState(song.tempo || '');
  const [expandedVersions, setExpandedVersions] = useState({});
  const [expandedActionPanel, setExpandedActionPanel] = useState(null); // 'lyrics' | 'files' | 'upload' | 'ideas' | 'history'
  const [previewFile, setPreviewFile] = useState(null);

  const versionsArray = getVersionsArray(song.versions);
  const latestVersion = versionsArray[0];
  const olderVersions = versionsArray.slice(1);
  const unreadCount = getUnreadCommentCount(song);
  const attachmentCount = song.attachments ? Object.keys(song.attachments).length : 0;

  const toggleVersionExpanded = (versionId) => {
    setExpandedVersions(prev => ({ ...prev, [versionId]: !prev[versionId] }));
  };

  const handleSaveName = async () => {
    const success = await onUpdateSongName(song.id, editName, editTempo);
    if (success) setEditingSong(false);
  };

  const toggleActionPanel = (panel) => {
    setExpandedActionPanel(prev => prev === panel ? null : panel);
  };

  const isDragged = draggedIndex === index;
  const showInsertBefore = insertIndex === index && draggedIndex !== null && draggedIndex !== index;
  const showInsertAfter = insertIndex === index + 1 && draggedIndex !== null && draggedIndex !== index;

  return (
    <div data-song-index={index}>
      {showInsertBefore && (
        <div className="h-1 bg-brand rounded-full mb-3 shadow-lg shadow-brand/50" />
      )}

      <div
        className={`card-3d border rounded-card overflow-hidden transition-all duration-200 ${
          isDragged ? 'border-brand ring-2 ring-brand/30' : 'border-transparent'
        }`}
      >
        {/* Song Header */}
        <div
          onClick={() => !editingSong && onToggleExpand(song.id)}
          className="p-4 flex items-center justify-between cursor-pointer hover:bg-white/5 transition"
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div
              className="cursor-grab active:cursor-grabbing p-1 -ml-1 touch-none"
              onMouseDown={(e) => onMouseDown(e, index)}
              onTouchStart={(e) => onTouchStart(e, index)}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
              onClick={(e) => e.stopPropagation()}
            >
              <GripVertical className="w-5 h-5 text-text-subtle" />
            </div>

            <div className="bg-brand p-2 rounded-full flex-shrink-0">
              <Music className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>

            <div className="min-w-0 flex-1">
              {editingSong ? (
                <div className="flex flex-col sm:flex-row gap-2" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyPress={(e) => { if (e.key === 'Enter') handleSaveName(); }}
                    placeholder="Song name"
                    className="flex-1 bg-transparent border border-surface-border-light rounded-card-btn px-3 py-2 text-white text-sm focus:outline-none focus:border-brand"
                    autoFocus
                  />
                  <input
                    type="number"
                    value={editTempo}
                    onChange={(e) => setEditTempo(e.target.value)}
                    placeholder="BPM"
                    min="1"
                    max="300"
                    className="w-20 bg-transparent border border-surface-border-light rounded-card-btn px-3 py-2 text-white text-sm focus:outline-none focus:border-brand"
                  />
                  <div className="flex gap-2">
                    <button onClick={handleSaveName} className="bg-brand hover:bg-brand/80 transition px-3 py-1 rounded text-sm">Save</button>
                    <button
                      onClick={() => {
                        setEditingSong(false);
                        setEditName(song.name);
                        setEditTempo(song.tempo || '');
                      }}
                      className="bg-transparent hover:bg-brand transition px-3 py-1 rounded text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-base sm:text-lg truncate">
                      {song.name}
                      {song.tempo && <span className="text-text-subtle font-normal"> • {song.tempo} BPM</span>}
                    </h3>
                    {unreadCount > 0 && (
                      <div className="flex items-center gap-1 bg-brand px-2 py-0.5 rounded-full">
                        <Bell className="w-3 h-3 text-white" />
                        <span className="text-xs font-semibold text-white">{unreadCount}</span>
                      </div>
                    )}
                  </div>
                  <p className="text-xs sm:text-sm text-text-subtle">
                    {versionsArray.length} version{versionsArray.length !== 1 ? 's' : ''}
                    {song.createdByName && ` • added by ${song.createdByName}`}
                  </p>
                </>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setEditingSong(true);
                setEditName(song.name);
                setEditTempo(song.tempo || '');
              }}
              className="bg-transparent hover:bg-brand transition p-2 flex-shrink-0 rounded-full"
            >
              <Edit2 className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm(`Delete "${song.name}" and all its versions?`)) {
                  onDeleteSong(song.id);
                }
              }}
              className="bg-transparent hover:bg-brand transition p-2 flex-shrink-0 rounded-full"
            >
              <Trash2 className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="border-t border-surface-border-light bg-white/5 p-4 sm:p-5">

            {/* HERO: Latest Version */}
            {latestVersion && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">Version {versionsArray.length}</span>
                    <span className="text-xs text-text-subtle">
                      {new Date(latestVersion.uploadDate).toLocaleDateString()}
                      {latestVersion.uploadedByName && ` • ${latestVersion.uploadedByName}`}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <a
                      href={latestVersion.fileURL}
                      download={latestVersion.fileName}
                      className="bg-transparent hover:bg-brand transition p-1.5 rounded-full"
                      title="Download"
                    >
                      <Download className="w-3.5 h-3.5 text-white" />
                    </a>
                    <button
                      onClick={() => {
                        if (window.confirm('Delete this version?')) {
                          onDeleteVersion(song.id, latestVersion.id, latestVersion.storagePath);
                        }
                      }}
                      className="bg-transparent hover:bg-red-600 transition p-1.5 rounded-full"
                      title="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5 text-white" />
                    </button>
                  </div>
                </div>
                <VersionPlayer
                  song={song}
                  version={latestVersion}
                  versionNumber={versionsArray.length}
                  isLatest={true}
                  user={user}
                  commenterName={commenterName}
                  onUpdateNotes={onUpdateNotes}
                  onAddComment={onAddComment}
                  onDeleteComment={onDeleteComment}
                />
              </div>
            )}

            {/* Action Bar */}
            <div className="flex flex-wrap gap-2 py-3 border-t border-surface-border-light">
              {/* Lyrics */}
              <button
                onClick={() => toggleActionPanel('lyrics')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition ${
                  expandedActionPanel === 'lyrics'
                    ? 'bg-brand text-white'
                    : (song.lyricsText || song.lyrics)
                      ? 'bg-white/10 text-white hover:bg-white/15'
                      : 'bg-transparent border border-surface-border-light text-white hover:border-surface-border'
                }`}
              >
                <FileText className="w-3.5 h-3.5" />
                {(song.lyricsText || song.lyrics) ? 'Lyrics' : '+ Lyrics'}
              </button>

              {/* Files (attachments) */}
              <button
                onClick={() => toggleActionPanel('files')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition ${
                  expandedActionPanel === 'files'
                    ? 'bg-brand text-white'
                    : attachmentCount > 0
                      ? 'bg-white/10 text-white hover:bg-white/15'
                      : 'bg-transparent border border-surface-border-light text-white hover:border-surface-border'
                }`}
              >
                <Paperclip className="w-3.5 h-3.5" />
                {attachmentCount > 0 ? `Docs (${attachmentCount})` : '+ Docs'}
              </button>

              {/* Upload version */}
              <button
                onClick={() => toggleActionPanel('upload')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition ${
                  expandedActionPanel === 'upload'
                    ? 'bg-brand text-white'
                    : 'bg-transparent border border-surface-border-light text-white hover:border-surface-border'
                }`}
              >
                <Upload className="w-3.5 h-3.5" />
                + Version
                {!isPremium && <span className="text-text-subtle ml-1">({getVersionCount(song)}/{FREE_VERSION_LIMIT})</span>}
              </button>

              {/* Ideas */}
              {ideas.length > 0 && (
                <button
                  onClick={() => toggleActionPanel('ideas')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition ${
                    expandedActionPanel === 'ideas'
                      ? 'bg-brand text-white'
                      : 'bg-transparent border border-surface-border-light text-white hover:border-surface-border'
                  }`}
                >
                  <Lightbulb className="w-3.5 h-3.5 text-yellow-400" />
                  Ideas ({ideas.length})
                </button>
              )}

              {/* History */}
              {olderVersions.length > 0 && (
                <button
                  onClick={() => toggleActionPanel('history')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition ${
                    expandedActionPanel === 'history'
                      ? 'bg-brand text-white'
                      : 'bg-transparent border border-surface-border-light text-white hover:border-surface-border'
                  }`}
                >
                  <Music className="w-3.5 h-3.5" />
                  History ({olderVersions.length})
                </button>
              )}
            </div>

            {/* Panels */}
            {expandedActionPanel === 'lyrics' && (
              <div className="mt-3">
                <SongLyricsPanel
                  song={song}
                  isMobile={isMobile}
                  loading={loading}
                  onUpdateLyricsText={onUpdateLyricsText}
                  onLyricsFileUpload={onLyricsUpload}
                  onDeleteLyricsFile={onDeleteLyrics}
                  onPreviewFile={setPreviewFile}
                />
              </div>
            )}

            {expandedActionPanel === 'files' && (
              <div className="mt-3 p-3 border border-surface-border-light rounded-card-btn">
                <SongFilesPanel
                  song={song}
                  isMobile={isMobile}
                  loading={loading}
                  onUploadSongFile={onUploadSongFile}
                  onDeleteSongFile={onDeleteSongFile}
                  onPreviewFile={setPreviewFile}
                />
              </div>
            )}

            {expandedActionPanel === 'upload' && (
              <div className="mt-3 p-3 border border-surface-border-light rounded-card-btn">
                {canAddVersion(song) ? (
                  <DragDropZone
                    accept="audio/*,video/mp4,.mp4,.m4a"
                    onFileSelect={(file) => onAudioUpload(song.id, file)}
                    disabled={loading}
                    icon={Upload}
                    message="Drop audio file (MP3, WAV, M4A, FLAC)"
                    isMobile={isMobile}
                  />
                ) : (
                  <p className="text-yellow-200 text-sm">
                    Free limit of {FREE_VERSION_LIMIT} versions reached. Upgrade to Premium for unlimited.
                  </p>
                )}
                {uploadProgress[`audio/${song.id}`] > 0 && uploadProgress[`audio/${song.id}`] < 100 && (
                  <div className="mt-2">
                    <div className="bg-white/10 rounded-full h-1.5 overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-brand to-brand-magenta h-full transition-all duration-300"
                        style={{ width: `${uploadProgress[`audio/${song.id}`]}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {expandedActionPanel === 'ideas' && (
              <div className="mt-3 p-3 border border-surface-border-light rounded-card-btn max-h-48 overflow-y-auto">
                <div className="space-y-2">
                  {ideas.map(idea => (
                    <div key={idea.id} className="flex items-center justify-between border border-surface-border-light rounded-card-btn p-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{idea.label}</p>
                        <p className="text-xs text-text-subtle">
                          {Math.floor(idea.duration / 60)}:{(idea.duration % 60).toString().padStart(2, '0')}
                        </p>
                      </div>
                      <button
                        onClick={() => onAddIdeaToSong(song.id, idea)}
                        disabled={addingFromIdea}
                        className="bg-brand hover:bg-brand/80 disabled:opacity-disabled transition px-2 py-1 text-xs font-medium rounded-full ml-2"
                      >
                        {addingFromIdea ? '...' : 'Add'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {expandedActionPanel === 'history' && (
              <div className="mt-3 space-y-2">
                {olderVersions.map((version, idx) => {
                  const versionNumber = versionsArray.length - 1 - idx;
                  const isVersionExpanded = expandedVersions[version.id];
                  const commentCount = version.comments ? version.comments.length : 0;

                  return (
                    <div key={version.id} className="border border-surface-border-light rounded-card-btn overflow-hidden">
                      <button
                        onClick={() => toggleVersionExpanded(version.id)}
                        className="w-full px-3 py-2 flex items-center justify-between hover:bg-white/5 transition text-left"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {isVersionExpanded ? (
                            <ChevronDown className="w-4 h-4 text-text-subtle" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-text-subtle" />
                          )}
                          <span className="text-sm font-medium">v{versionNumber}</span>
                          <span className="text-xs text-text-subtle truncate">
                            {new Date(version.uploadDate).toLocaleDateString()}
                          </span>
                        </div>
                        {commentCount > 0 && (
                          <span className="flex items-center gap-1 text-xs text-text-subtle bg-white/10 px-1.5 py-0.5 rounded-full">
                            <MessageCircle className="w-3 h-3" />
                            {commentCount}
                          </span>
                        )}
                      </button>
                      {isVersionExpanded && (
                        <div className="border-t border-surface-border-light p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-text-subtle">{version.fileName}</span>
                            <div className="flex gap-1">
                              <a
                                href={version.fileURL}
                                download={version.fileName}
                                className="bg-transparent hover:bg-brand transition p-1.5 rounded-full"
                              >
                                <Download className="w-3 h-3 text-white" />
                              </a>
                              <button
                                onClick={() => {
                                  if (window.confirm('Delete?')) {
                                    onDeleteVersion(song.id, version.id, version.storagePath);
                                  }
                                }}
                                className="bg-transparent hover:bg-red-600 transition p-1.5 rounded-full"
                              >
                                <Trash2 className="w-3 h-3 text-white" />
                              </button>
                            </div>
                          </div>
                          <VersionPlayer
                            song={song}
                            version={version}
                            versionNumber={versionNumber}
                            isLatest={false}
                            user={user}
                            commenterName={commenterName}
                            onUpdateNotes={onUpdateNotes}
                            onAddComment={onAddComment}
                            onDeleteComment={onDeleteComment}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {showInsertAfter && (
        <div className="h-1 bg-brand rounded-full mt-3 shadow-lg shadow-brand/50" />
      )}

      {/* Preview modal */}
      {previewFile && (
        <Suspense fallback={null}>
          <DocumentPreview file={previewFile} onClose={() => setPreviewFile(null)} />
        </Suspense>
      )}
    </div>
  );
}
